import { useCallback, useEffect, useRef, useState } from "react";
import { getObjectStoreIdString } from "../../decorators/StoreIdHelper";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import { DocFieldConfig } from "../../models/config/DocFieldConfig";
import DocTypeConfig from "../../models/config/DocTypeConfig";
import { HighlightColours } from "../../models/HighlightColors";
import { Hydratable } from "../../models/hydration/Hydratable";
import { DlfMatcher } from "../../models/matchers/DlfMatcher";
import { MatcherFactory } from "../../models/matchers/factory/MatcherFactory";
import Model from "../../models/Model";
import {
  COUNTER_VAR,
  OCRFILENAME_VAR,
  SOURCEFILENAME_VAR,
  UUID_VAR,
} from "../../models/transformation/TransformRule";
import {
  validationRuleSetToValidationRules,
  validationRulesToValidationRuleSet,
} from "../../models/validation/ValidationRuleSet";
import "../../styles/dlfEditing/dlfEditorList.css";
import { Button } from "../buttons/Button";
import DLFTabList from "../DLFTabList";
import { HydratableDocError } from "../errorScreens/HydratableDocError";
import { HydratableModelError } from "../errorScreens/HydratableModelError";
import { useAsync } from "../hooks/useAsync";
import { usePageDispatch, usePageState } from "../pageProvider/PageProvider";
import { HighlightedSpans } from "../SpansHighlightable";
// import SpansHighlightable from '../SpansHighlightable';
import { Spinner } from "../Spinner";
import { AdvancedTooltip } from "../tooltips/AdvancedTooltip";
import {
  TTDlfGenerateReuseAll,
  TTDlfRequireAllLabel,
  TTTransformAddRule,
} from "../tooltips/ConfigurationTooltips";
import { Footer } from "../transformation/Footer";
import { TransformationEditorList } from "../transformation/TransformationEditorList";
import { ValidationEditorHeader } from "../validation/ValidationEditorHeader";
import { ValidationEditorList } from "../validation/ValidationEditorList";
import { ValidationRuleEditor } from "../validation/ValidationRuleEditor";
import { AcceleratorDocFieldMatcherEditor } from "./AcceleratorDocFieldMatcherEditor";
import { DlfEditorHeader } from "./DlfEditorHeader";

export { DlfEditorList };

type Props = {
  docTypeConfig: DocTypeConfig;
  acceleratorDoc: Hydratable<AcceleratorDoc> | undefined;
  model: Hydratable<Model> | undefined;
  modelChanged: (modified: Model) => void;
  view: "extraction" | "transformation" | "validation";
  hideHighlights?: boolean;
};

const isDlfVisible = (
  docFieldConfig: DocFieldConfig,
  selectedTabs: string[],
): boolean => {
  return (
    !docFieldConfig.actualHidden && selectedTabs.includes(docFieldConfig.displayTab)
  );
};

const DlfEditorList = (props: Props) => {
  const pageDispatch = usePageDispatch();
  const pageState = usePageState();
  const [selectedTabs, setSelectedTabs] = useState<string[]>([]);
  const [openField, setOpenField] = useState<string | undefined>(undefined);
  const effectiveOpenField = props.hideHighlights ? undefined : openField;

  useEffect(() => {
    if (props.hideHighlights) {
      setOpenField(undefined);
    }
  }, [props.hideHighlights]);

  const acceleratorDoc = props.acceleratorDoc?.isHydrated()
    ? props.acceleratorDoc.getHydrated()
    : undefined;
  const model = props.model?.isHydrated()
    ? props.model.getHydrated()
    : undefined;

  const getPageSpansById = useRef(
    acceleratorDoc?.getPageSpansById.bind(acceleratorDoc),
  ).current!;
  const pageIds = useRef(
    (acceleratorDoc?.pages ?? []).map((x) => x.externalPageIdentifier),
  ).current!;

  /* Change any number of DLF Matchers by specifying the absolute reuseIndex of an ExtractedFieldData.
   * If the DLF Editor is open in this component, the PageContext's pageIndex will be changed to the extracted values
   * page index.
   * TODO: change name to something like "setDlfMatcherFromEFD" ?
   */
  const changeToReuse = useCallback(
    (
      updates: Array<{ field: string; index: number }>,
      source: "MANUAL" | "AUTO",
    ) => {
      if (!acceleratorDoc || !model) return;
      let jumpBaseField = undefined;
      let actuallyUpdated = false;
      for (const update of updates) {
        // never jump to page if more than one update provided
        const jumpToPage = updates.length > 1 ? false : source === "MANUAL";
        // also need Field Name
        const extracedFieldData = acceleratorDoc.getExtractedFieldData(
          update.field,
        );
        if (!extracedFieldData) {
          console.debug(
            "Could not process changeToReuse() request for field " +
              update.field,
          );
          continue;
        }

        const oldDlfMatcher = model.getDlfMatcher(update.field);

        const baseField = extracedFieldData.getReuseAt(update.index);
        if (!baseField) {
          console.log(
            `Invalid reuse index ${update.index} for DLF ${update.field}`,
          );
          continue;
        }
        if (baseField.extractedBy(oldDlfMatcher)) {
          console.log(
            `DLF ${update.field} already extracted by Models DLF ${oldDlfMatcher}, DLF ${baseField}`,
          );
          continue;
        }
        // get matcher data from baseField, update model
        model.updateDlf(MatcherFactory.createDlfMatcher(baseField.matcherData));
        extracedFieldData.setIndex(update.index);
        actuallyUpdated = true;
        // jump to page
        if (jumpToPage) {
          jumpBaseField = baseField;
        }
      }
      if (actuallyUpdated) {
        if (jumpBaseField && jumpBaseField.pageId !== "") {
          pageDispatch(
            acceleratorDoc.getPageIndex(jumpBaseField?.pageId ?? ""),
          );
        }
        props.modelChanged(model);
      }
    },
    [props.acceleratorDoc, props.model, props.modelChanged, openField],
  );

  const generateReuseForAll = async () => {
    if (!acceleratorDoc) return;
    // determine DLFs to generate REuse for
    const reuseCandidates = Array.from(
      acceleratorDoc.dlfs.get(acceleratorDoc.classifiedDocType)?.entries() ??
        [],
    ).filter(
      (x) =>
        x[1].getExtracted() == undefined ||
        ["ERROR", "BLANK", "NO_STATUS"].includes(
          x[1].getExtracted()!.fieldStatus,
        ),
    );

    console.log(reuseCandidates);

    const promises = reuseCandidates.map((x) =>
      acceleratorDoc.generateDlfReuse(x[0] as string),
    );
    // filter out invalid reuse updates after promise finished
    const generatedReuse = (await Promise.all(promises)).filter(
      (x) => x.length > 0,
    );
    changeToReuse(
      generatedReuse.map((x) => ({ field: x![0]!.fieldName!, index: 1 })),
      "AUTO",
    );
  };

  const [generatedReuse, generateReuseTrigger, generatingReuse] = useAsync(
    generateReuseForAll,
    null,
  );

  // if no hydratable provided, no doc selected
  if (!acceleratorDoc) {
    return <HydratableDocError acceleratorDoc={props.acceleratorDoc} />;
  }
  if (!model) {
    return <HydratableModelError model={props.model} />;
  }

  if (generatingReuse) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Spinner />
      </div>
    );
  }

  props.docTypeConfig.fields.sort((a, b) => a.position - b.position);

  const matchers = props.docTypeConfig.fields
    .filter((field) => isDlfVisible(field, selectedTabs))
    .map((field: DocFieldConfig, index: number) => model.getDlfMatcher(field)!)
    .filter((matcher) => matcher != undefined);

  return (
    <div className={"dlfEditorList"}>
      <div className={"top"}>
        <DLFTabList
          docType={props.docTypeConfig}
          tabsChanged={(modifiedTabs: string[]) => {
            setSelectedTabs(modifiedTabs);
          }}
        />

        <div className={"fluffer"} />

        <AdvancedTooltip overflow tooltipText={TTDlfGenerateReuseAll}>
          {(tProps) => (
            <Button
              {...tProps}
              hidden={matchers.length === 0}
              onClick={() => generateReuseTrigger()}
              disabled={generatingReuse}
            >
              Generate Reuse
            </Button>
          )}
        </AdvancedTooltip>
      </div>
      <table style={{ width: "100%" }}>
        <tbody>
          {matchers.map((matcher: DlfMatcher, index: number) => {
            const updateMatcher = (newDlfMatcher: DlfMatcher) => {
              // TODO: move model.updateDlf call out of this component
              model.updateDlf(newDlfMatcher);
              props.modelChanged(model);
            };

            const key = `${getObjectStoreIdString(acceleratorDoc)}-${matcher.getDiscriminator()}-${index}`;
            const extractedFieldData = acceleratorDoc.getExtractedFieldData(
              matcher.fieldName,
            )!;
            const docFieldConfig = props.docTypeConfig.getDocFieldConfig(
              matcher.fieldName,
            )!;

            return (
              <DlfEditorHeader
                extractedData={extractedFieldData}
                key={matcher.fieldName}
                dlfMatcher={matcher}
                docFieldConfig={docFieldConfig}
                acceleratorDoc={acceleratorDoc}
                index={index}
                changeSelectedReuse={(reuseIndex: number) =>
                  changeToReuse(
                    [{ field: matcher.fieldName, index: reuseIndex }],
                    "MANUAL",
                  )
                }
                open={effectiveOpenField === matcher.fieldName}
                changeOpen={(open) =>
                  setOpenField(open ? matcher.fieldName : undefined)
                }
                updateMatcher={(matcher: DlfMatcher) => updateMatcher(matcher)}
              >
                {/* {props.view === 'extraction' && ( */}
                <div
                  className={"dlfEditor"}
                  style={{
                    display: props.view === "extraction" ? "initial" : "none",
                  }}
                >
                  <AcceleratorDocFieldMatcherEditor
                    key={key}
                    docFieldConfig={docFieldConfig}
                    acceleratorDoc={acceleratorDoc}
                    extractedData={extractedFieldData}
                    pageIndex={pageState.pageIndex}
                    index={index}
                    labelColor={"black"}
                    labelBgColor={HighlightColours.getHighlightColor(index)}
                    matcher={matcher}
                    open={
                      props.view === "extraction" &&
                      effectiveOpenField === matcher.fieldName
                    }
                    otherOpen={
                      props.hideHighlights ||
                      (props.view === "extraction" && effectiveOpenField != undefined)
                    }
                    update={(matcher: DlfMatcher) => updateMatcher(matcher)}
                  />

                  {effectiveOpenField === matcher.fieldName && (
                    <HighlightedSpans
                      key={
                        getObjectStoreIdString(acceleratorDoc) +
                        acceleratorDoc.pages.join(":")
                      }
                      pageIds={pageIds}
                      getPageSpansById={getPageSpansById}
                      groupColors={[HighlightColours.getColor(index)]}
                      selectedSpans={
                        extractedFieldData.getAtIndex()?.spanIds
                          ? [extractedFieldData.getAtIndex()!.spanIds]
                          : undefined
                      }
                    />
                  )}
                </div>
                {/* )} */}
                {props.view === "transformation" && (
                  <TransformationEditorList
                    mainListTitle="Template Transform Rules"
                    nonEditableVariables={[
                      COUNTER_VAR,
                      OCRFILENAME_VAR,
                      SOURCEFILENAME_VAR,
                      UUID_VAR,
                    ]}
                    effectiveRules={docFieldConfig.transformationRules}
                    testValue={
                      acceleratorDoc
                        .getExtractedFieldData(matcher.fieldName)
                        .getAtIndex()?.fieldValue ?? ""
                    }
                    variables={[OCRFILENAME_VAR, SOURCEFILENAME_VAR, UUID_VAR]}
                    fields={props.docTypeConfig.fields}
                    fieldName={matcher.fieldName}
                    open={true}
                    rules={model.getTransformRules(matcher.fieldName)}
                    save={(rules) => {
                      model.setTransformRules(matcher.fieldName, rules);
                      props.modelChanged(model);
                    }}
                    setOpen={() => null}
                  />
                )}
                {props.view === "validation" && (
                  <ValidationEditorList
                    mainListTitle={"Template Validation Rules"}
                    fieldName={matcher.fieldName}
                    open={true}
                    validationOperator={
                      model.getValidationRuleSet(matcher.fieldName)
                        .clientValidationOperator
                    }
                    rules={validationRuleSetToValidationRules(
                      model.getValidationRuleSet(matcher.fieldName),
                    )}
                    testValue={
                      acceleratorDoc
                        .getExtractedFieldData(matcher.fieldName)
                        .getExtracted()?.fieldValue
                    }
                    effectiveRulesOperator={
                      docFieldConfig.validator.clientValidationOperator
                    }
                    effectiveRules={docFieldConfig.validator.validationRules}
                    update={(rules, operator) => {
                      model.setValidationRuleSet(
                        matcher.fieldName,
                        validationRulesToValidationRuleSet(
                          matcher.fieldName,
                          rules,
                          operator,
                        ),
                      );
                      props.modelChanged(model);
                    }}
                    setOpen={() => null}
                  />
                )}
              </DlfEditorHeader>
            );
          })}
          {matchers.length === 0 && (
            <tr>
              <td>No fields available in this document type.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
